/**
 * WaitlistQuickConfirmPage
 *
 * Public-Page (KEIN Login nötig). Wird vom "Slot frei"-Email-Link aufgerufen:
 *   /waitlist/quick-confirm?slot=<slot_id>&token=<customer_confirm_token>
 *
 * Flow:
 *   1. Lade Slot-Vorschau via GET /waitlist/quick-confirm/<slot_id>/preview?token=...
 *   2. Zeige Service, Datum, Uhrzeit
 *   3. User tippt "Termin bestätigen" → POST /waitlist/quick-confirm/<slot_id>?token=...
 *   4. Erfolg → Bestätigungs-Screen mit "Termin gebucht ✓"
 */
import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Loader2, CheckCircle2, XCircle, Calendar, Clock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (iso) =>
  new Date(iso).toLocaleDateString("de-CH", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });

const fmtTime = (iso) =>
  new Date(iso).toLocaleTimeString("de-CH", {
    hour: "2-digit",
    minute: "2-digit",
  });

export default function WaitlistQuickConfirmPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Zwei Link-Formate werden unterstützt:
  //   1. E-Mail-Link: `?slot=<slot_id>&token=<token>`
  //   2. WhatsApp-Link (kombiniert wegen Meta-URL-Button-Limit):
  //      `?t=<slot_id>.<token>` ODER der Inhalt ist im `slot`-Param
  let slotId = searchParams.get("slot");
  let token = searchParams.get("token");
  const combined = searchParams.get("t");
  if (combined && combined.includes(".")) {
    const [s, t] = combined.split(".");
    if (!slotId) slotId = s;
    if (!token) token = t;
  } else if (!token && slotId && slotId.includes(".")) {
    // Falls jemand `?slot=<slot>.<token>` geschickt hat
    const [s, t] = slotId.split(".");
    slotId = s;
    token = t;
  }

  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [slot, setSlot] = useState(null);
  const [confirmed, setConfirmed] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const load = async () => {
      if (!slotId || !token) {
        setError("Ungültiger Link.");
        setLoading(false);
        return;
      }
      try {
        const res = await axios.get(
          `${API}/waitlist/quick-confirm/${slotId}/preview`,
          { params: { token } }
        );
        const data = res.data;
        if (data.status === "already_booked") {
          setError(
            data.by_self
              ? "Du hast diesen Termin bereits bestätigt."
              : "Dieser Termin wurde leider schon von jemand anderem gebucht."
          );
        } else if (data.status === "expired") {
          setError("Dieser Buchungslink ist abgelaufen.");
        } else if (data.status === "available") {
          setSlot(data);
        } else {
          setError("Slot konnte nicht geladen werden.");
        }
      } catch (e) {
        setError(
          e.response?.data?.detail ||
            "Link ungültig oder bereits verwendet."
        );
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [slotId, token]);

  const handleConfirm = async () => {
    setConfirming(true);
    try {
      await axios.post(
        `${API}/waitlist/quick-confirm/${slotId}`,
        {},
        { params: { token } }
      );
      setConfirmed(true);
    } catch (e) {
      setError(
        e.response?.data?.detail ||
          "Fehler bei der Bestätigung. Bitte versuche es erneut."
      );
    } finally {
      setConfirming(false);
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "#0a0a0a",
        color: "#f5f5f5",
        padding: 16,
        fontFamily:
          "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      }}
      data-testid="waitlist-quick-confirm-page"
    >
      <div
        style={{
          background: "#161616",
          borderRadius: 18,
          padding: "32px 28px",
          maxWidth: 420,
          width: "100%",
          border: "1px solid #2a2a2a",
          boxShadow: "0 8px 28px rgba(0,0,0,0.5)",
        }}
      >
        {loading ? (
          <div style={{ textAlign: "center", padding: "40px 0" }}>
            <Loader2
              size={36}
              style={{
                color: "#facc15",
                animation: "spin 1s linear infinite",
              }}
            />
            <p style={{ marginTop: 16, color: "#aaa", fontSize: 14 }}>
              Lade Termin-Daten…
            </p>
          </div>
        ) : confirmed ? (
          <div style={{ textAlign: "center" }}>
            <CheckCircle2 size={56} style={{ color: "#22c55e" }} />
            <h2
              style={{
                fontSize: 22,
                marginTop: 14,
                fontWeight: 800,
                color: "#fff",
              }}
              data-testid="confirm-success-title"
            >
              Termin gebucht ✓
            </h2>
            <p style={{ marginTop: 10, color: "#aaa", fontSize: 14 }}>
              Du erhältst gleich eine Bestätigungs-E-Mail mit allen Details.
            </p>
            <button
              onClick={() => navigate("/portal")}
              style={{
                marginTop: 24,
                background: "#facc15",
                color: "#000",
                border: "none",
                borderRadius: 10,
                padding: "12px 24px",
                fontWeight: 700,
                fontSize: 14,
                cursor: "pointer",
                width: "100%",
              }}
              data-testid="goto-portal-btn"
            >
              Zum Kundenportal
            </button>
          </div>
        ) : error ? (
          <div style={{ textAlign: "center" }}>
            <XCircle size={56} style={{ color: "#f87171" }} />
            <h2
              style={{
                fontSize: 19,
                marginTop: 14,
                fontWeight: 700,
                color: "#fff",
              }}
              data-testid="confirm-error-title"
            >
              Buchung nicht möglich
            </h2>
            <p style={{ marginTop: 10, color: "#aaa", fontSize: 14 }}>
              {error}
            </p>
            <button
              onClick={() => navigate("/portal")}
              style={{
                marginTop: 24,
                background: "transparent",
                color: "#aaa",
                border: "1px solid #333",
                borderRadius: 10,
                padding: "12px 24px",
                fontWeight: 600,
                fontSize: 14,
                cursor: "pointer",
                width: "100%",
              }}
            >
              Zum Kundenportal
            </button>
          </div>
        ) : (
          slot && (
            <div>
              <h2
                style={{
                  fontSize: 22,
                  fontWeight: 800,
                  color: "#fff",
                  marginBottom: 6,
                  textAlign: "center",
                }}
              >
                Termin bestätigen
              </h2>
              <p
                style={{
                  color: "#aaa",
                  fontSize: 13,
                  textAlign: "center",
                  marginBottom: 22,
                }}
              >
                Ein Termin auf deiner Warteliste ist frei geworden.
              </p>
              <div
                style={{
                  background: "#0d0d0d",
                  borderRadius: 12,
                  padding: 18,
                  marginBottom: 22,
                  border: "1px solid #2a2a2a",
                }}
              >
                <div
                  style={{
                    fontSize: 16,
                    fontWeight: 700,
                    color: "#facc15",
                    marginBottom: 12,
                  }}
                  data-testid="slot-service-name"
                >
                  {slot.service_name}
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    color: "#ddd",
                    fontSize: 14,
                    marginBottom: 6,
                  }}
                >
                  <Calendar size={16} style={{ color: "#facc15" }} />
                  <span data-testid="slot-date">
                    {fmtDate(slot.slot_datetime)}
                  </span>
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    color: "#ddd",
                    fontSize: 14,
                  }}
                >
                  <Clock size={16} style={{ color: "#facc15" }} />
                  <span data-testid="slot-time">
                    {fmtTime(slot.slot_datetime)} Uhr
                  </span>
                </div>
              </div>
              <button
                onClick={handleConfirm}
                disabled={confirming}
                style={{
                  width: "100%",
                  background: confirming ? "#71717a" : "#facc15",
                  color: "#000",
                  border: "none",
                  borderRadius: 12,
                  padding: "14px 24px",
                  fontWeight: 800,
                  fontSize: 15,
                  cursor: confirming ? "not-allowed" : "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                }}
                data-testid="confirm-booking-btn"
              >
                {confirming ? (
                  <>
                    <Loader2
                      size={18}
                      style={{ animation: "spin 1s linear infinite" }}
                    />
                    Wird gebucht…
                  </>
                ) : (
                  <>✓ Termin verbindlich buchen</>
                )}
              </button>
              <p
                style={{
                  fontSize: 11,
                  color: "#666",
                  textAlign: "center",
                  marginTop: 14,
                  lineHeight: 1.5,
                }}
              >
                Mit dem Klick wird der Termin verbindlich für dich reserviert.
                Du kannst ihn später im Kundenportal absagen.
              </p>
            </div>
          )
        )}
      </div>
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
